import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CallToActionSection = () => {
  const nextSteps = [
    {
      icon: "FileText",
      title: "Download AI Core Blueprint",
      description: "Get our comprehensive methodology for achieving AI independence",
      action: "Download Free Blueprint",
      link: "/ai-core-blueprint"
    },
    {
      icon: "Calendar",
      title: "Schedule Strategic Consultation",
      description: "Discuss your AI independence journey with our experts",
      action: "Book Consultation",
      link: "/get-started"
    },
    {
      icon: "BookOpen",
      title: "Explore Resources",
      description: "Access our library of AI independence resources and case studies",
      action: "Browse Resources",
      link: "/resources-hub"
    }
  ];

  const contactInfo = [
    {
      icon: "Mail",
      label: "Email",
      value: "hello@aicleancore.com",
      link: "mailto:hello@aicleancore.com"
    },
    {
      icon: "Phone",
      label: "Phone",
      value: "+1 (619) 800-1235",
      link: "tel:+16198001235"
    },
    {
      icon: "MapPin",
      label: "Headquarters",
      value: "San Francisco, CA",
      link: "#"
    }
  ];

  return (
    <section className="py-20 lg:py-32 bg-gradient-to-br from-brand-primary via-blue-800 to-brand-secondary">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center text-white mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Ready to Reclaim Your
            <span className="block text-brand-accent">AI Independence?</span>
          </h2>
          
          <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
            Join 500+ organizations that have broken free from vendor dependency and built sustainable AI capabilities with our proven methodology.
          </p>
        </div>

        {/* Next Steps */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {nextSteps?.map((step, index) => (
            <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20 hover:bg-white/15 transition-all duration-300">
              <div className="w-16 h-16 bg-brand-accent/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name={step?.icon} size={32} className="text-brand-accent" />
              </div>
              
              <h3 className="text-xl font-bold text-white mb-4 text-center">
                {step?.title}
              </h3>
              
              <p className="text-blue-100 text-center mb-6 leading-relaxed">
                {step?.description}
              </p>
              
              <div className="text-center">
                <Link to={step?.link}>
                  <Button
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white/10 hover:border-white/50"
                    iconName="ArrowRight"
                    iconPosition="right"
                  >
                    {step?.action}
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Contact Information */}
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 lg:p-12 border border-white/20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-bold text-white mb-6">
                Let's Start Your AI Independence Journey
              </h3>
              
              <p className="text-blue-100 mb-8 leading-relaxed">
                Every day you remain dependent on proprietary AI vendors is another day your competitors could be building sustainable advantages. The time for AI independence is now.
              </p>
              
              <div className="space-y-4">
                {contactInfo?.map((contact, index) => (
                  <div key={index} className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-brand-accent/20 rounded-lg flex items-center justify-center">
                      <Icon name={contact?.icon} size={20} className="text-brand-accent" />
                    </div>
                    <div>
                      <div className="text-blue-200 text-sm">{contact?.label}</div>
                      {contact?.link !== "#" ? (
                        <a 
                          href={contact?.link}
                          className="text-white font-medium hover:text-brand-accent transition-colors duration-200"
                        >
                          {contact?.value}
                        </a>
                      ) : (
                        <div className="text-white font-medium">{contact?.value}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="text-center lg:text-right">
              <div className="inline-flex flex-col space-y-4">
                <Link to="/get-started">
                  <Button
                    variant="default"
                    size="lg"
                    className="bg-brand-accent hover:bg-brand-accent/90 text-brand-primary font-semibold"
                    iconName="Rocket"
                    iconPosition="right"
                  >
                    Start Your Transformation
                  </Button>
                </Link>
                
                <Link to="/ai-core-blueprint">
                  <Button
                    variant="outline"
                    size="lg"
                    className="border-white/30 text-white hover:bg-white/10 hover:border-white/50"
                    iconName="Download"
                    iconPosition="right"
                  >
                    Get Free Blueprint
                  </Button>
                </Link>
              </div>
              
              <p className="text-blue-200 text-sm mt-6">
                No vendor lock-in. No hidden dependencies.<br />
                Just pure AI independence.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CallToActionSection;